import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LaunchIcon from '@mui/icons-material/Launch';
import { Section } from './DetailPrimitives';
import { BINDS_VIA_LABEL, OntologyKind, formatDate } from './ontologyFormat';
import { agentHref, normalizeUsage, ontologyErrorMessage, usageApiForKind } from '../../api/ontologyApi';
import { dsVar, ink } from '../../theme/tokens';

export interface UsageFamily {
    family_code: string;
    latest_code: string;
    latest_name?: string | null;
    variant_count: number;
    variants: { activity_template_code: string; created_at?: string | null }[];
    paths: string[];
    binds_via?: string | null;
}

/**
 * The list of agent clone families, with their configuration paths and binding form.
 *
 * Extracted so the source-document impact panel renders an identical block. A document's
 * "agents affected" and a concept's "used by these agents" are the SAME finding reached by
 * a different route, and two hand-maintained copies of this markup is exactly how they
 * start looking like two different things to a reader comparing them.
 */
export const AgentFamilyList: React.FC<{ families: UsageFamily[] }> = ({ families }) => {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    return (
        <Box sx={{ border: `1px solid ${dsVar('line')}`, borderRadius: '6px', overflow: 'hidden' }}>
            {families.map((family, index) => {
                const isOpen = Boolean(expanded[family.family_code]);
                const hasVariants = family.variant_count > 1;

                return (
                    <Box
                        key={family.family_code}
                        sx={{ borderTop: index === 0 ? 'none' : `1px solid ${dsVar('line')}` }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 1.5, py: 1.25 }}>
                            <Box
                                onClick={hasVariants
                                    ? () => setExpanded((e) => ({
                                        ...e, [family.family_code]: !e[family.family_code],
                                    }))
                                    : undefined}
                                sx={{
                                    width: 18, flexShrink: 0, display: 'flex', mt: '2px',
                                    cursor: hasVariants ? 'pointer' : 'default',
                                }}
                            >
                                {hasVariants && (isOpen
                                    ? <ExpandMoreIcon sx={{ fontSize: 18, color: dsVar('fg-muted') }} />
                                    : <ChevronRightIcon sx={{ fontSize: 18, color: dsVar('fg-muted') }} />)}
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                {/* Opens the agents-catalog drawer on this template. */}
                                <Box
                                    component="a"
                                    href={agentHref(family.latest_code)}
                                    sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                        fontSize: '0.875rem', color: dsVar('accent-fg'), textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                    }}
                                >
                                    {family.latest_name || family.family_code}
                                    <LaunchIcon sx={{ fontSize: 13 }} />
                                </Box>

                                {/* The configuration path is the substance of the answer:
                                    it is *where* the change lands. */}
                                {family.paths.map((path) => (
                                    <Typography
                                        key={path}
                                        component="div"
                                        sx={{
                                            fontFamily: '"JetBrains Mono", monospace',
                                            fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: 0.25,
                                            wordBreak: 'break-all',
                                        }}
                                    >
                                        {path}
                                    </Typography>
                                ))}

                                {hasVariants && (
                                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: 0.25 }}>
                                        {family.variant_count} versions
                                    </Typography>
                                )}
                            </Box>

                            {family.binds_via && (
                                <Typography
                                    component="span"
                                    sx={{
                                        flexShrink: 0,
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontSize: '0.625rem', letterSpacing: '0.04em',
                                        textTransform: 'uppercase', color: dsVar('fg-muted'), mt: '3px',
                                    }}
                                >
                                    {BINDS_VIA_LABEL[family.binds_via] ?? family.binds_via}
                                </Typography>
                            )}
                        </Box>

                        <Collapse in={isOpen} unmountOnExit>
                            <Box sx={{ pl: 4.5, pr: 1.5, pb: 1.25 }}>
                                {family.variants.map((variant) => (
                                    <Box
                                        key={variant.activity_template_code}
                                        sx={{
                                            display: 'flex', alignItems: 'baseline',
                                            justifyContent: 'space-between', gap: 1.5,
                                            py: 0.5, borderTop: `1px solid ${dsVar('line')}`,
                                        }}
                                    >
                                        <Box
                                            component="a"
                                            href={agentHref(variant.activity_template_code)}
                                            sx={{
                                                fontFamily: '"JetBrains Mono", monospace',
                                                fontSize: '0.6875rem', color: dsVar('fg-2'),
                                                textDecoration: 'none', wordBreak: 'break-all',
                                                '&:hover': { textDecoration: 'underline', color: dsVar('accent-fg') },
                                            }}
                                        >
                                            {variant.activity_template_code}
                                        </Box>
                                        <Typography sx={{ flexShrink: 0, fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                                            {formatDate(variant.created_at)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Collapse>
                    </Box>
                );
            })}
        </Box>
    );
};

/**
 * Which activity templates use this concept, policy or parameter — the reverse of the
 * agents drawer, and the answer to "what breaks if I change this?".
 *
 * Derived server-side by walking template configurations rather than stored, so it
 * cannot drift from what the templates actually say.
 *
 * Templates are grouped into CLONE FAMILIES. A concept cited by three timestamped
 * versions of one template is used by one agent, not three; on the dev instance that
 * collapse takes six rows down to two. Variants stay reachable behind a disclosure so an
 * audit of a specific version is still possible.
 *
 * On a POLICY the relationship is indirect — a policy names no template, so this is
 * "templates citing a concept this policy governs". The heading says so; implying a
 * direct association would be wrong.
 */
const UsedByTemplatesSection: React.FC<{ kind: OntologyKind; code: string }> = ({ kind, code }) => {
    const [families, setFamilies] = useState<UsageFamily[]>([]);
    const [templateCount, setTemplateCount] = useState(0);
    const [via, setVia] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchUsage = usageApiForKind(kind);
        if (!fetchUsage || !code) return undefined;

        const controller = new AbortController();
        let active = true;

        (async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetchUsage(code, { signal: controller.signal });
                if (!active) return;
                const usage = normalizeUsage(res);
                setFamilies(usage.families as UsageFamily[]);
                setTemplateCount(usage.templateCount);
                setVia(usage.via);
            } catch (err: any) {
                if (!active || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                console.error(`Error loading usage for ${kind} ${code}:`, err);
                setError(ontologyErrorMessage(err, 'Could not load where this is used.'));
            } finally {
                if (active) setLoading(false);
            }
        })();

        return () => { active = false; controller.abort(); };
    }, [kind, code]);

    const heading = via === 'governs_concepts'
        ? 'Used by these agents, via the concepts it governs'
        : 'Used by these agents';

    if (loading) {
        return (
            <Section title={heading} always>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: dsVar('fg-2') }}>
                    <CircularProgress size={14} sx={{ color: dsVar('accent-fg') }} />
                    <Typography sx={{ fontSize: '0.8125rem' }}>Checking activity templates…</Typography>
                </Box>
            </Section>
        );
    }

    if (error) {
        return (
            <Section title={heading} always>
                <Typography sx={{ fontSize: '0.8125rem', color: ink('#FCA5A5') }}>{error}</Typography>
            </Section>
        );
    }

    // Not an anomaly: 154 of 270 concepts on the dev instance are cited by no template.
    // Stating it is more useful than an empty box.
    if (families.length === 0) {
        const noun = kind === 'concepts' ? 'concept' : kind === 'policies' ? 'policy' : 'parameter';
        return (
            <Section title={heading} always>
                <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-2') }}>
                    No activity template uses this {noun}.
                    {kind === 'parameters' && ' It will not be applied by any node.'}
                </Typography>
            </Section>
        );
    }

    return (
        <Section title={heading} always>
            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), mb: 1.5 }}>
                {families.length} agent{families.length === 1 ? '' : 's'}
                {templateCount !== families.length && (
                    <Typography component="span" sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>
                        {' '}· {templateCount} template versions
                    </Typography>
                )}
            </Typography>

            <AgentFamilyList families={families} />
        </Section>
    );
};

export default UsedByTemplatesSection;
